import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { Reveal, MaskLines, ImageReveal } from "../lib/motion";
import { ArcButton } from "../components/site/Brand";
import { ProjectCard } from "../components/site/ProjectCard";
import { Marquee } from "../components/site/Marquee";
import { CTASection } from "../components/site/CTASection";
import { IMG } from "../data/site";
import { PROJECTS } from "../data/projects";
import { SERVICES, APPROACH } from "../data/services";
import { ARTICLES, formatDate } from "../data/journal";

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden bg-charcoal" data-testid="hero">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <motion.img
          src={IMG.hero}
          alt="A calm, light-filled London residential interior with a central chandelier."
          className="h-full w-full object-cover"
          style={{ objectPosition: "50% 38%" }}
          initial={reduce ? { opacity: 0 } : { scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/25 to-charcoal/85" />
      <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/70 via-transparent to-transparent" />

      <div className="relative z-10 h-full container-x flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          className="font-serif text-chalk font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-brand-title"
        >
          Arc <span className="text-bronze px-[0.06em]">&amp;</span> Hollow
        </motion.p>
        <motion.p
          className="overline text-chalk/70 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          Interior Architecture &amp; Design · London
        </motion.p>
        <h1 className="font-serif text-chalk font-light text-[13vw] leading-[0.98] sm:text-6xl lg:text-8xl max-w-5xl tracking-tight">
          <MaskLines lines={["Interiors shaped", "from the inside out."]} delay={0.5} />
        </h1>
        <motion.p
          className="mt-8 max-w-xl text-chalk/80 font-light text-base md:text-lg leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
        >
          Arc &amp; Hollow is a London interior architecture and design studio creating
          considered residential interiors—from spatial planning and architectural detailing
          to furniture, art and final installation.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
        >
          <ArcButton to="/projects" variant="primary" onDark data-testid="hero-explore">
            Explore our work <ArrowRight size={15} strokeWidth={1.6} />
          </ArcButton>
          <ArcButton to="/contact" variant="outline" onDark data-testid="hero-contact">
            Start a conversation
          </ArcButton>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-chalk/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
      >
        <span className="overline hidden sm:block">Scroll</span>
        <motion.span animate={reduce ? {} : { y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
          <ArrowDown size={18} strokeWidth={1.4} />
        </motion.span>
      </motion.div>
    </section>
  );
};

const BrandStatement = () => (
  <section className="container-x py-24 md:py-40" data-testid="brand-statement">
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
      <Reveal className="lg:col-span-3">
        <p className="overline text-bronze">The hidden layers<br />define the experience</p>
      </Reveal>
      <div className="lg:col-span-9">
        <MaskLines
          as="h2"
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.25]"
          lines={["A home is shaped by more than its", "visible surfaces."]}
        />
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-olive">
            Proportion, circulation, light and material junctions determine how a home is
            experienced. We consider these elements alongside furniture, art and the rhythms of
            everyday life to create interiors with warmth, clarity and lasting character.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const SelectedProjects = () => (
  <section className="bg-limestone/40 py-24 md:py-32" data-testid="selected-projects">
    <div className="container-x">
      <div className="flex items-end justify-between mb-14">
        <div>
          <Reveal><p className="overline text-bronze">Selected work</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light">Recent projects</h2>
          </Reveal>
        </div>
        <Link to="/projects" className="hidden md:inline-flex items-center gap-2 link-underline overline text-charcoal" data-testid="projects-view-all">
          View all <ArrowRight size={14} strokeWidth={1.6} />
        </Link>
      </div>
      <div className="grid gap-x-8 gap-y-14 md:grid-cols-3">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.slug} project={p} index={i} />
        ))}
      </div>
      <div className="mt-12 md:hidden">
        <ArcButton to="/projects" variant="outline" className="w-full">View all projects</ArcButton>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="container-x py-24 md:py-36" data-testid="services-preview">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Reveal><p className="overline text-bronze">What we do</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight">
            A single studio, from concept to final styling
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <ArcButton to="/services" variant="ghost" className="mt-8 !px-0">
            All services <ArrowRight size={15} strokeWidth={1.6} />
          </ArcButton>
        </Reveal>
      </div>
      <div className="lg:col-span-8 lg:pl-8">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.08}>
            <Link to="/services" className="group block border-t border-charcoal/15 py-8 md:py-10" data-testid={`service-preview-${s.slug}`}>
              <div className="flex items-start gap-6">
                <span className="overline text-bronze mt-2">{s.index}</span>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl md:text-3xl font-light group-hover:text-bronze transition-colors duration-500">
                    {s.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-olive font-light leading-relaxed">{s.cardSummary}</p>
                </div>
                <ArrowRight size={20} strokeWidth={1.3} className="mt-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 shrink-0" />
              </div>
            </Link>
          </Reveal>
        ))}
        <div className="border-t border-charcoal/15" />
      </div>
    </div>
  </section>
);

const ApproachPreview = () => (
  <section className="bg-charcoal text-chalk py-24 md:py-36" data-testid="approach-preview">
    <div className="container-x">
      <Reveal><p className="overline text-bronze">The approach</p></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light max-w-2xl">
          Four movements, considered as one
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-y-12 gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
        {APPROACH.map((a, i) => (
          <Reveal key={a.n} delay={i * 0.1} className="border-t border-chalk/20 pt-6">
            <span className="font-serif text-5xl font-light text-bronze">{a.n}</span>
            <h3 className="mt-6 text-xl font-medium tracking-wide">{a.title}</h3>
            <p className="mt-3 text-sm text-chalk/60 font-light leading-relaxed">{a.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// Preserved for later restoration — temporarily not rendered on the public homepage.
// eslint-disable-next-line no-unused-vars
const JournalPreview = () => (
  <section className="container-x py-24 md:py-36" data-testid="journal-preview">
    <div className="flex items-end justify-between mb-14">
      <div>
        <Reveal><p className="overline text-bronze">Journal</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light">Notes from the studio</h2>
        </Reveal>
      </div>
      <Link to="/journal" className="hidden md:inline-flex items-center gap-2 link-underline overline" data-testid="journal-view-all">
        All entries <ArrowRight size={14} strokeWidth={1.6} />
      </Link>
    </div>
    <div className="grid gap-x-8 gap-y-12 md:grid-cols-3">
      {ARTICLES.map((a, i) => (
        <Reveal key={a.slug} delay={(i % 3) * 0.08}>
          <Link to={`/journal/${a.slug}`} className="group block" data-testid={`journal-card-${a.slug}`}>
            <ImageReveal src={a.hero} alt={a.title} className="aspect-[4/3]" imgClassName="transition-transform duration-[1200ms] group-hover:scale-105" />
            <p className="mt-5 overline text-bronze">{a.category} · {a.readingTime}</p>
            <h3 className="mt-3 font-serif text-2xl font-light group-hover:text-bronze transition-colors duration-500">{a.title}</h3>
            <p className="mt-2 text-sm text-olive font-light leading-relaxed">{a.excerpt}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Arc & Hollow | Interior Architecture & Residential Design, London"
        description="Arc & Hollow is a London interior architecture and design studio creating considered residential interiors through architecture, materiality, art and bespoke detail."
        path="/"
      />
      <Hero />
      <BrandStatement />
      <SelectedProjects />
      <div className="bg-chalk py-10 md:py-16 border-y border-charcoal/10">
        <Marquee items={["Architecture", "Materiality", "Craft", "Light", "Detail"]} />
      </div>
      <ServicesPreview />
      <ApproachPreview />
      {/* Journal section temporarily hidden from the public homepage — preserved for later restoration. Re-add <JournalPreview /> here to restore. */}
      <CTASection />
    </>
  );
}
